import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { navigateAssistantHref } from "../utils/assistantNavigation";

/**
 * Lista de opções do assistente.
 *
 * - `selectable` / `choice_options`: clique principal envia `reply` no chat
 *   (não navega — preserva o contexto do diálogo).
 * - Ícone externo abre o registro (no popup-equipe: shell na mesma janela;
 *   fora do shell, nova aba — igual à busca).
 * - Sem selectable: comportamento legado (navega no app).
 */
export default function AssistantRecordLinksCard({ artifact, onChoose, onNavigate }) {
  const { t } = useTranslation("shell");
  const navigate = useNavigate();
  const location = useLocation();

  const items = Array.isArray(artifact?.items)
    ? artifact.items.filter((i) => i && (i.reply || i.href || i.id != null))
    : [];
  if (!items.length) return null;

  const selectable =
    artifact?.kind === "choice_options" ||
    artifact?.selectable === true ||
    items.some((i) => i.reply);

  const goHref = (href, { closeAssistant = false } = {}) => {
    if (!href) return;
    navigateAssistantHref(href, {
      pathname: location.pathname,
      navigate,
      onBeforeNavigate: () => {
        if (closeAssistant) onNavigate?.();
      },
    });
  };

  const choose = (item) => {
    const text = String(item.reply || "").trim();
    if (text && typeof onChoose === "function") {
      onChoose(text);
      return;
    }
    if (item.href) {
      goHref(item.href, { closeAssistant: true });
    }
  };

  const openExternal = (e, href) => {
    e.preventDefault();
    e.stopPropagation();
    // Ícone: não fecha o assistente (usuário ainda pode escolher no chat).
    goHref(href, { closeAssistant: false });
  };

  return (
    <Box
      sx={{
        mt: 0.75,
        p: 1.25,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        bgcolor: "background.paper",
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
        {artifact.title ||
          t("platformAi.recordLinksTitle", { defaultValue: "Registros encontrados" })}
      </Typography>
      {(artifact.hint || selectable) && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {artifact.hint ||
            t("platformAi.choiceHint", {
              defaultValue: "Toque em uma opção abaixo ou digite o número no chat.",
            })}
        </Typography>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {items.slice(0, 20).map((item, index) => {
          const n = item.optionIndex != null ? Number(item.optionIndex) : index + 1;
          const label = item.label || `#${item.id}`;
          return (
            <Box
              key={`${item.id ?? n}-${label}`}
              sx={{
                display: "flex",
                alignItems: "stretch",
                gap: 0.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
                bgcolor: "action.hover",
              }}
            >
              <Button
                size="small"
                variant="text"
                onClick={() => choose(item)}
                sx={{
                  flex: 1,
                  justifyContent: "flex-start",
                  alignItems: item.description ? "flex-start" : "center",
                  textTransform: "none",
                  px: 1,
                  py: 0.85,
                  borderRadius: 0,
                  color: "text.primary",
                  minWidth: 0,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 22,
                    height: 22,
                    mr: 1,
                    mt: item.description ? 0.15 : 0,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {n}
                </Box>
                <Box sx={{ minWidth: 0, textAlign: "left" }}>
                  <Typography variant="body2" sx={{ lineHeight: 1.35, fontWeight: 600 }}>
                    {label}
                  </Typography>
                  {item.description ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        mt: 0.35,
                        lineHeight: 1.35,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {item.description}
                    </Typography>
                  ) : null}
                </Box>
              </Button>
              {item.href ? (
                <IconButton
                  size="small"
                  aria-label={t("platformAi.openRecord", { defaultValue: "Abrir na tela" })}
                  title={t("platformAi.openRecordNewTab", {
                    defaultValue: "Abrir registro",
                  })}
                  onClick={(e) => openExternal(e, item.href)}
                  sx={{ borderRadius: 0, px: 1 }}
                >
                  <OpenInNewIcon fontSize="inherit" />
                </IconButton>
              ) : null}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
